import { useState, useEffect, useRef, useMemo } from "react";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

/* ════════════════════════════════════════════════════════════════
   SkillsShowcase — Three.js 3D Constellation + Typing + Progress
   ════════════════════════════════════════════════════════════════ */

/* ─── Skill nodes ─── */
const SKILLS = [
  { name: "React.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", cat: "frontend", prof: 90, info: "4 yrs · Production apps" },
  { name: "TypeScript", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", cat: "frontend", prof: 85, info: "3 yrs · Daily driver" },
  { name: "Angular", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/angular/angular-original.svg", cat: "frontend", prof: 72, info: "2 yrs · Frontend framework" },
  { name: "Node.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", cat: "backend", prof: 88, info: "4 yrs · Backend services" },
  { name: "Python", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", cat: "backend", prof: 82, info: "3 yrs · AI/ML & APIs" },
  { name: "Java", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/java/java-original.svg", cat: "backend", prof: 80, info: "3 yrs · Enterprise apps" },
  { name: ".NET", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/dot-net/dot-net-original.svg", cat: "backend", prof: 65, info: "1 yr · Web APIs" },
  { name: "Docker", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", cat: "devops", prof: 75, info: "2 yrs · Containerization" },
  { name: "AWS", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", cat: "devops", prof: 78, info: "2 yrs · Cloud infrastructure" },
  { name: "Git", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/git/git-original.svg", cat: "devops", prof: 90, info: "4 yrs · Version control" },
  { name: "MongoDB", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg", cat: "database", prof: 80, info: "3 yrs · NoSQL databases" },
  { name: "MySQL", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg", cat: "database", prof: 80, info: "3 yrs · Relational data" },
];

const CAT_COLORS = {
  frontend: new THREE.Color(0x06b6d4),
  backend: new THREE.Color(0x10b981),
  devops: new THREE.Color(0xf59e0b),
  database: new THREE.Color(0x8b5cf6),
};

const CONNECTIONS = [
  [0,1],[0,2],[1,2],
  [3,4],[3,5],[3,6],[4,5],
  [7,8],[7,9],[8,9],
  [10,11],
  [0,3],[1,3],[4,10],[3,10],[5,11],
];

/* ─── Helpers ─── */
function fibSphere(n, radius) {
  const pts = [];
  const g = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const t = g * i;
    pts.push(new THREE.Vector3(Math.cos(t) * r * radius, y * radius, Math.sin(t) * r * radius));
  }
  return pts;
}

function makeGlowTex() {
  const s = 128, cv = document.createElement("canvas");
  cv.width = cv.height = s;
  const cx = cv.getContext("2d");
  const gr = cx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  gr.addColorStop(0, "rgba(255,255,255,0.7)");
  gr.addColorStop(0.2, "rgba(255,255,255,0.25)");
  gr.addColorStop(0.5, "rgba(255,255,255,0.06)");
  gr.addColorStop(1, "rgba(0,0,0,0)");
  cx.fillStyle = gr;
  cx.fillRect(0, 0, s, s);
  return new THREE.CanvasTexture(cv);
}

/* ─── Center orb shaders ─── */
const ORB_V = `
varying vec3 vN,vP;
void main(){
  vN=normalize(normalMatrix*normal);vP=position;
  gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);
}`;

const ORB_F = `
uniform float uTime;varying vec3 vN,vP;
void main(){
  float f=pow(1.-abs(dot(vN,vec3(0,0,1))),2.5);
  vec3 c1=vec3(.024,.714,.831),c2=vec3(.925,.286,.596);
  float w=sin(vP.y*8.+uTime*1.5)*.5+.5;
  vec3 col=mix(c1,c2,w);
  float p=.7+sin(uTime*2.)*.1;
  gl_FragColor=vec4(col*(f*1.3+.25),(f*.6+.08)*p);
}`;

/* ─── Typing sub-component ─── */
const TypingBadge = ({ text, onDone }) => {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => {
    let i = 0;
    const tick = () => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) { setDone(true); setTimeout(onDone, 800); return; }
      setTimeout(tick, 155 + (Math.random() - 0.5) * 80);
    };
    const id = setTimeout(tick, 80);
    return () => clearTimeout(id);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <span className={`ss-type-badge ${done ? "done" : "active"}`}>
      <span>{displayed}</span>
      {!done && <span className="ss-cursor" />}
    </span>
  );
};

/* ─── Domain Icons Helper ─── */
const DomainIcon = ({ domainKey }) => {
  switch (domainKey) {
    case "frontend":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ss-card-icon">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      );
    case "backend":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ss-card-icon">
          <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
          <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
          <line x1="6" y1="6" x2="6.01" y2="6" />
          <line x1="6" y1="18" x2="6.01" y2="18" />
        </svg>
      );
    case "devops":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ss-card-icon">
          <path d="M12 12c-2-2.67-4-4-6-4a4 4 0 1 0 0 8c2 0 4-1.33 6-4Zm0 0c2 2.67 4 4 6 4a4 4 0 1 0 0-8c-2 0-4 1.33-6 4Z" />
        </svg>
      );
    case "database":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ss-card-icon">
          <ellipse cx="12" cy="5" rx="9" ry="3" />
          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
          <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
        </svg>
      );
    case "sysdesign":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ss-card-icon">
          <circle cx="12" cy="5" r="3" />
          <circle cx="6" cy="19" r="3" />
          <circle cx="18" cy="19" r="3" />
          <path d="M12 8v8M12 8L7.5 16.5M12 8l4.5 8.5" />
        </svg>
      );
    case "testing":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ss-card-icon">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="m9 11 2 2 4-4" />
        </svg>
      );
    default:
      return null;
  }
};

/* ════════════════════════════════════════════════════════════════
   Main Component
   ════════════════════════════════════════════════════════════════ */
export const SkillsShowcase = () => {
  const [headerVisible, setHeaderVisible] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, name: "", icon: "", info: "" });
  const [typingStarted, setTypingStarted] = useState(false);
  const [typingIdx, setTypingIdx] = useState(0);
  const [progressFired, setProgressFired] = useState(false);
  const [progressValues, setProgressValues] = useState([0, 0, 0, 0, 0, 0]);
  const [progressLabels, setProgressLabels] = useState([0, 0, 0, 0, 0, 0]);

  const headerRef = useRef(null);
  const threeRef = useRef(null);
  const labelsRef = useRef(null);
  const tooltipRef = useRef(null);
  const typingRef = useRef(null);
  const progressRef = useRef(null);

  const typingSkills = useMemo(() => [
    "JavaScript", "TypeScript", "React", "Next.js", "Node.js",
    "Python", "FastAPI", "PostgreSQL", "Redis", "Docker",
    "Kubernetes", "AWS", "CI/CD", "REST APIs", "GraphQL", "Git",
  ], []);

  const progressData = useMemo(() => [
    { key: "frontend", name: "Frontend Development", target: 90, color: "#06b6d4", tags: "React, TypeScript, Next.js, HTML5/CSS3" },
    { key: "backend", name: "Backend Development", target: 85, color: "#10b981", tags: "Node.js, Express, Python, Java, REST APIs" },
    { key: "devops", name: "DevOps & CI/CD", target: 75, color: "#f59e0b", tags: "Docker, AWS, Git, CI/CD Pipelines" },
    { key: "database", name: "Databases", target: 80, color: "#8b5cf6", tags: "MongoDB, MySQL, PostgreSQL, Redis" },
    { key: "sysdesign", name: "System Design", target: 70, color: "#ec4899", tags: "Architecture, Scalability, Microservices" },
    { key: "testing", name: "Testing & QA", target: 65, color: "#34d399", tags: "Jest, React Testing Library, Cypress, QA" },
  ], []);

  /* ══════════════════ Three.js Scene ══════════════════ */
  useEffect(() => {
    const container = threeRef.current;
    const labelsContainer = labelsRef.current;
    if (!container || !labelsContainer) return;

    /* StrictMode safety — clean slate */
    while (labelsContainer.firstChild) labelsContainer.removeChild(labelsContainer.firstChild);

    let W = container.clientWidth;
    let H = container.clientHeight;
    if (!W || !H) return;
    const DPR = Math.min(window.devicePixelRatio, 1.5);

    /* ── Scene & camera ── */
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x050510, 0.028);

    const camera = new THREE.PerspectiveCamera(60, W / H, 0.1, 100);
    camera.position.set(0, 0.5, 7);

    /* ── Renderer ── */
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true, powerPreference: 'high-performance' });
    renderer.setSize(W, H);
    renderer.setPixelRatio(DPR);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.insertBefore(renderer.domElement, labelsContainer);

    /* ── Post-processing (bloom) ── */
    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    const bloom = new UnrealBloomPass(
      new THREE.Vector2(Math.round(W / 2), Math.round(H / 2)), 0.9, 0.55, 0.25
    );
    composer.addPass(bloom);

    /* ── Lights ── */
    scene.add(new THREE.AmbientLight(0x223344, 0.6));
    const l1 = new THREE.PointLight(0x06b6d4, 2.2, 20);
    l1.position.set(5, 5, 5);
    scene.add(l1);
    const l2 = new THREE.PointLight(0xec4899, 1.2, 16);
    l2.position.set(-4, -3, 4);
    scene.add(l2);
    /* Rim / back light for 3D depth */
    const l3 = new THREE.PointLight(0x8b5cf6, 1.0, 14);
    l3.position.set(0, -4, -5);
    scene.add(l3);
    const l4 = new THREE.DirectionalLight(0xffffff, 0.4);
    l4.position.set(2, 8, 3);
    scene.add(l4);

    /* ── Main group ── */
    const group = new THREE.Group();
    scene.add(group);

    /* ── Center energy orb ── */
    const orbMat = new THREE.ShaderMaterial({
      vertexShader: ORB_V, fragmentShader: ORB_F,
      uniforms: { uTime: { value: 0 } },
      transparent: true, side: THREE.DoubleSide, depthWrite: false,
    });
    group.add(new THREE.Mesh(new THREE.SphereGeometry(0.55, 64, 64), orbMat));

    /* Center glow sprite */
    const glowTex = makeGlowTex();
    const cGlow = new THREE.Sprite(new THREE.SpriteMaterial({
      map: glowTex, color: 0x06b6d4, transparent: true, opacity: 0.22,
      blending: THREE.AdditiveBlending, depthWrite: false,
    }));
    cGlow.scale.set(2.5, 2.5, 1);
    group.add(cGlow);

    /* ── Orbital rings ── */
    const ringData = [
      { r: 1.8, rot: [Math.PI / 2 + 0.3, 0, 0] },
      { r: 2.8, rot: [Math.PI / 2 - 0.2, 0.3, 0] },
      { r: 3.8, rot: [Math.PI / 2 + 0.1, -0.2, 0.4] },
    ];
    const ringMeshes = ringData.map(({ r, rot }) => {
      const m = new THREE.Mesh(
        new THREE.TorusGeometry(r, 0.008, 8, 120),
        new THREE.MeshBasicMaterial({
          color: 0x06b6d4, transparent: true, opacity: 0.07,
          side: THREE.DoubleSide, depthWrite: false,
        })
      );
      m.rotation.set(...rot);
      group.add(m);
      return m;
    });

    /* ── Skill node positions (Fibonacci sphere) ── */
    const positions = fibSphere(SKILLS.length, 3.2);

    /* ── Skill meshes ── */
    const nodeGeo = new THREE.SphereGeometry(0.24, 24, 24);
    const nodeMeshes = SKILLS.map((skill, i) => {
      const col = CAT_COLORS[skill.cat];
      const mesh = new THREE.Mesh(nodeGeo, new THREE.MeshStandardMaterial({
        color: col.clone().multiplyScalar(0.4),
        emissive: col,
        emissiveIntensity: 0.35,
        metalness: 0.85,
        roughness: 0.15,
      }));
      mesh.position.copy(positions[i]);
      mesh.userData = { idx: i, base: positions[i].clone() };
      mesh.castShadow = true;
      group.add(mesh);

      /* halo sprite */
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({
        map: glowTex, color: col, transparent: true, opacity: 0.18,
        blending: THREE.AdditiveBlending, depthWrite: false,
      }));
      sp.position.copy(positions[i]);
      sp.scale.set(0.85, 0.85, 1);
      sp.userData.pidx = i;
      group.add(sp);

      return mesh;
    });

    const glowSprites = group.children.filter(c => c.isSprite && c.userData.pidx !== undefined);

    /* ── Connection lines ── */
    const seen = new Set();
    const conns = CONNECTIONS.filter(([a, b]) => {
      const k = Math.min(a, b) + "-" + Math.max(a, b);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    const lPos = new Float32Array(conns.length * 6);
    const lCol = new Float32Array(conns.length * 6);
    conns.forEach(([a, b], i) => {
      const pa = positions[a], pb = positions[b];
      lPos.set([pa.x, pa.y, pa.z, pb.x, pb.y, pb.z], i * 6);
      const avg = CAT_COLORS[SKILLS[a].cat].clone().lerp(CAT_COLORS[SKILLS[b].cat], 0.5);
      lCol.set([avg.r, avg.g, avg.b, avg.r, avg.g, avg.b], i * 6);
    });
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute("position", new THREE.BufferAttribute(lPos, 3));
    lineGeo.setAttribute("color", new THREE.BufferAttribute(lCol, 3));
    const lineMat = new THREE.LineBasicMaterial({
      vertexColors: true, transparent: true, opacity: 0.12,
      depthWrite: false, blending: THREE.AdditiveBlending,
    });
    group.add(new THREE.LineSegments(lineGeo, lineMat));

    /* ── Energy pulse waves ── */
    const pulseGeo = new THREE.RingGeometry(0.5, 0.55, 32);
    const pulses = [0x06b6d4, 0xec4899].map(c => {
      const mat = new THREE.MeshBasicMaterial({
        color: c, transparent: true, opacity: 0,
        side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending,
      });
      const m = new THREE.Mesh(pulseGeo, mat);
      group.add(m);
      return m;
    });

    /* ── Background particles ── */
    const PC = 800;
    const pp = new Float32Array(PC * 3);
    const pcol = new Float32Array(PC * 3);
    for (let i = 0; i < PC; i++) {
      const r = 8 + Math.random() * 18;
      const th = Math.random() * Math.PI * 2;
      const ph = Math.acos(2 * Math.random() - 1);
      pp.set([r * Math.sin(ph) * Math.cos(th), r * Math.sin(ph) * Math.sin(th), r * Math.cos(ph)], i * 3);
      const b = 0.3 + Math.random() * 0.4;
      pcol.set([b * 0.9, b, b * (0.6 + Math.random() * 0.4)], i * 3);
    }
    const ptGeo = new THREE.BufferGeometry();
    ptGeo.setAttribute("position", new THREE.BufferAttribute(pp, 3));
    ptGeo.setAttribute("color", new THREE.BufferAttribute(pcol, 3));
    const particles = new THREE.Points(ptGeo, new THREE.PointsMaterial({
      size: 0.03, vertexColors: true, transparent: true, opacity: 0.45,
      sizeAttenuation: true, depthWrite: false, blending: THREE.AdditiveBlending,
    }));
    scene.add(particles);

    /* ── HTML labels ── */
    const labelEls = SKILLS.map(skill => {
      const d = document.createElement("div");
      d.className = "ss-3d-label";
      d.innerHTML = `<img class="ss-3d-logo" src="${skill.icon}" alt="${skill.name}" /><span>${skill.name}</span>`;
      labelsContainer.appendChild(d);
      return d;
    });
    const centerLbl = document.createElement("div");
    centerLbl.className = "ss-3d-center";
    centerLbl.textContent = "US";
    labelsContainer.appendChild(centerLbl);

    /* ── Mouse tracking ── */
    const mouse = { x: 0, y: 0, cx: 0, cy: 0 };
    const ray = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    let hovered = -1;

    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      mouse.cx = e.clientX;
      mouse.cy = e.clientY;
      ndc.set(mouse.x, mouse.y);
      if (tooltipRef.current) {
        tooltipRef.current.style.left = e.clientX + "px";
        tooltipRef.current.style.top = e.clientY + "px";
      }
    };
    const onLeave = () => {
      mouse.x = 0; mouse.y = 0;
      if (hovered >= 0) { hovered = -1; setTooltip(p => ({ ...p, visible: false })); }
    };
    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseleave", onLeave);

    /* ── Animation loop ── */
    const clock = new THREE.Clock();
    const _v = new THREE.Vector3();
    const _s = new THREE.Vector3();
    let sceneIn = false, sceneInT = 0, frame;
    let isVisible = true;
    let rayFrame = 0;

    /* Pause when off-screen */
    const visObs = new IntersectionObserver(([e]) => { isVisible = e.isIntersecting; }, { threshold: 0 });
    visObs.observe(container);

    const loop = () => {
      frame = requestAnimationFrame(loop);
      if (!isVisible) return;
      const t = clock.getElapsedTime();

      /* Entry animation */
      if (!sceneIn) {
        const rect = container.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.85 && rect.bottom > 0) {
          sceneIn = true;
          sceneInT = t;
        }
      }
      const entry = sceneIn ? Math.min((t - sceneInT) / 1.8, 1) : 0;
      const ease = 1 - Math.pow(1 - entry, 4);
      group.scale.setScalar(ease);

      /* Orb shader */
      orbMat.uniforms.uTime.value = t;

      /* Rotate group */
      group.rotation.y = t * 0.08;
      group.rotation.x = Math.sin(t * 0.04) * 0.1;

      /* Rings */
      ringMeshes.forEach((r, i) => { r.rotation.z = t * (0.06 + i * 0.03); });

      /* Float nodes */
      nodeMeshes.forEach((m, i) => {
        const b = m.userData.base;
        m.position.y = b.y + Math.sin(t * 0.7 + i * 1.3) * 0.06;
        m.rotation.x = t * 0.3 + i;
        m.rotation.y = t * 0.2 + i * 0.5;
      });

      /* Sync glow sprites */
      glowSprites.forEach(sp => sp.position.copy(nodeMeshes[sp.userData.pidx].position));

      /* Camera parallax */
      camera.position.x += (mouse.x * 1.0 - camera.position.x) * 0.015;
      camera.position.y += (mouse.y * 0.6 + 0.5 - camera.position.y) * 0.015;
      camera.lookAt(0, 0, 0);

      /* Raycaster (throttled to every 3rd frame) */
      rayFrame++;
      if (rayFrame % 3 === 0) {
        ray.setFromCamera(ndc, camera);
        const hits = ray.intersectObjects(nodeMeshes);
        const newH = hits.length > 0 ? hits[0].object.userData.idx : -1;
        if (newH !== hovered) {
          hovered = newH;
          container.style.cursor = hovered >= 0 ? "pointer" : "default";
          if (hovered >= 0) {
            const s = SKILLS[hovered];
            setTooltip({ visible: true, name: s.name, icon: s.icon, info: s.info });
          } else {
            setTooltip(p => ({ ...p, visible: false }));
          }
        }
      }

      /* Highlight hovered node */
      nodeMeshes.forEach((m, i) => {
        const isH = i === hovered;
        m.material.emissiveIntensity += ((isH ? 1.2 : 0.35) - m.material.emissiveIntensity) * 0.1;
        _s.setScalar(isH ? 1.6 : 1);
        m.scale.lerp(_s, 0.1);
      });
      lineMat.opacity += ((hovered >= 0 ? 0.35 : 0.12) - lineMat.opacity) * 0.08;

      /* Pulses */
      pulses.forEach((p, i) => {
        const phase = ((t + i * 2.2) % 4.5) / 4.5;
        p.scale.setScalar(1 + phase * 5);
        p.material.opacity = (1 - phase) * 0.06;
        p.lookAt(camera.position);
      });

      /* Center glow pulse */
      cGlow.material.opacity = 0.2 + Math.sin(t * 1.5) * 0.08;
      cGlow.scale.setScalar(2.4 + Math.sin(t * 1.2) * 0.25);

      /* Particles */
      particles.rotation.y = t * 0.008;
      particles.rotation.x = t * 0.004;

      /* Labels projection */
      labelEls.forEach((el, i) => {
        const m = nodeMeshes[i];
        m.getWorldPosition(_v);
        _v.y += 0.32;
        _v.project(camera);
        if (_v.z > 0 && _v.z < 1) {
          el.style.transform = `translate(-50%,-50%) translate(${(_v.x * .5 + .5) * W}px,${(-_v.y * .5 + .5) * H}px)`;
          el.style.opacity = i === hovered ? "1" : "0.65";
          el.style.display = "";
        } else {
          el.style.display = "none";
        }
      });

      /* Center label */
      _v.set(0, 0, 0).applyMatrix4(group.matrixWorld).project(camera);
      centerLbl.style.transform = `translate(-50%,-50%) translate(${(_v.x * .5 + .5) * W}px,${(-_v.y * .5 + .5) * H}px)`;

      composer.render();
    };
    loop();

    /* ── Resize ── */
    const onResize = () => {
      W = container.clientWidth;
      H = container.clientHeight;
      camera.aspect = W / H;
      camera.updateProjectionMatrix();
      renderer.setSize(W, H);
      composer.setSize(W, H);
    };
    window.addEventListener("resize", onResize);

    /* ── Cleanup ── */
    return () => {
      cancelAnimationFrame(frame);
      visObs.disconnect();
      window.removeEventListener("resize", onResize);
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseleave", onLeave);
      renderer.dispose();
      composer.dispose();
      scene.traverse(o => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) {
          (Array.isArray(o.material) ? o.material : [o.material]).forEach(m => m.dispose());
        }
      });
      glowTex.dispose();
      labelEls.forEach(el => el.remove());
      centerLbl.remove();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Header reveal ── */
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setHeaderVisible(true), { threshold: 0.3 });
    if (headerRef.current) obs.observe(headerRef.current);
    return () => obs.disconnect();
  }, []);

  /* ── Typing trigger ── */
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setTypingStarted(true), 500); obs.disconnect(); }
    }, { threshold: 0.3 });
    if (typingRef.current) obs.observe(typingRef.current);
    return () => obs.disconnect();
  }, []);

  /* ── Progress trigger ── */
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !progressFired) { setProgressFired(true); obs.disconnect(); }
    }, { threshold: 0.25 });
    if (progressRef.current) obs.observe(progressRef.current);
    return () => obs.disconnect();
  }, [progressFired]);

  /* ── Progress animation ── */
  useEffect(() => {
    if (!progressFired) return;
    const targets = progressData.map(d => d.target);
    const start = performance.now();
    const tick = (now) => {
      const vals = targets.map((tgt, i) => {
        const el = now - start - i * 120;
        if (el < 0) return 0;
        const p = Math.min(el / 1200, 1);
        return Math.round(tgt * (1 - Math.pow(1 - p, 4)));
      });
      setProgressValues(vals);
      setProgressLabels(vals);
      if (vals.some((v, i) => v < targets[i])) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [progressFired, progressData]);

  /* ═══════════════════════ JSX ═══════════════════════ */
  return (
    <section className="ss-section" id="skills-showcase">
      <div className="container" style={{ position: "relative", zIndex: 1 }}>
        {/* ━━ Header ━━ */}
        <div className={`ss-header ${headerVisible ? "ss-hdr-vis" : ""}`} ref={headerRef}>
          <div className="ss-tag"><span className="ss-tag-dot" />Skills &amp; Tech Stack</div>
          <h2 className="ss-main-title">
            <span className="ss-tw ss-tw1">Technologies</span>{" "}
            <span className="gradient-text ss-tw ss-tw2">I</span>{" "}
            <span className="gradient-text ss-tw ss-tw3">Work With</span>
          </h2>
          <p className="ss-subtitle">Full-stack capabilities across the modern development ecosystem</p>
        </div>
      </div>

      {/* ━━ 01 / 3D CONSTELLATION ━━ */}
      <div className="ss-sub ss-three-section">
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <span className="ss-sub-label">01 / CONSTELLATION</span>
          <h3 className="ss-sub-title">Technology Universe</h3>
        </div>
        <div ref={threeRef} className="ss-three-wrap">
          <div ref={labelsRef} className="ss-three-labels" />
        </div>
        <p className="ss-hint-3d">✦ Hover any node to explore</p>
      </div>

      {/* Tooltip */}
      <div ref={tooltipRef} className={`ss-3d-tip ${tooltip.visible ? "vis" : ""}`}>
        <img className="ss-3d-tip-logo" src={tooltip.icon} alt={tooltip.name} />
        <div className="ss-3d-tip-name">{tooltip.name}</div>
        <div className="ss-3d-tip-info">{tooltip.info}</div>
      </div>

      <div className="container" style={{ position: "relative", zIndex: 1 }}>
        <div className="ss-divider" />

        {/* ━━ 02 / TYPING ━━ */}
        <div className="ss-sub">
          <span className="ss-sub-label">02 / TYPE</span>
          <h3 className="ss-sub-title">Live Skill Roster</h3>
          <div className="ss-term-bar">
            <span className="ss-dots">
              <span className="ss-dot ss-dot-r" />
              <span className="ss-dot ss-dot-y" />
              <span className="ss-dot ss-dot-g" />
            </span>
            <span>$ skill.list --verbose</span>
          </div>
          <div className="ss-type-container" ref={typingRef}>
            {typingStarted && typingSkills.slice(0, typingIdx + 1).map((skill, i) =>
              i < typingIdx
                ? <span className="ss-type-badge done" key={skill}>{skill}</span>
                : <TypingBadge key={skill} text={skill} onDone={() => setTypingIdx(p => p + 1)} />
            )}
          </div>
        </div>

        <div className="ss-divider" />

        {/* ━━ 03 / PROGRESS ━━ */}
        <div className="ss-sub" ref={progressRef}>
          <span className="ss-sub-label">03 / PROFICIENCY</span>
          <h3 className="ss-sub-title">Expertise Levels</h3>
          <div className="ss-prog-grid">
            {progressData.map((skill, i) => {
              const r = 34;
              const stroke = 5.5;
              const circumference = r * 2 * Math.PI;
              const strokeDashoffset = circumference - (progressValues[i] / 100) * circumference;

              return (
                <div 
                  className="ss-prog-card" 
                  key={skill.name}
                  style={{
                    '--card-color': skill.color,
                    '--card-color-alpha': `${skill.color}22`,
                    '--card-color-alpha2': `${skill.color}11`,
                    '--card-color-glow': `${skill.color}33`,
                    animationDelay: `${i * 0.08}s`,
                  }}
                >
                  <div className="ss-card-top">
                    <div className="ss-card-icon-wrap">
                      <DomainIcon domainKey={skill.key} />
                    </div>
                    
                    <div className="ss-circle-wrap">
                      <svg viewBox="0 0 80 80" className="ss-circle-svg">
                        <circle
                          stroke="rgba(255, 255, 255, 0.04)"
                          fill="transparent"
                          strokeWidth={stroke}
                          r={r}
                          cx="40"
                          cy="40"
                        />
                        <circle
                          stroke={skill.color}
                          fill="transparent"
                          strokeWidth={stroke}
                          strokeDasharray={`${circumference} ${circumference}`}
                          style={{ strokeDashoffset }}
                          strokeLinecap="round"
                          className="ss-circle-progress"
                          r={r}
                          cx="40"
                          cy="40"
                        />
                      </svg>
                      <span className="ss-circle-pct">{progressLabels[i]}%</span>
                    </div>
                  </div>

                  <div className="ss-card-bottom">
                    <h4 className="ss-card-title">{skill.name}</h4>
                    <p className="ss-card-subtitle">{skill.tags}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ━━ INLINE STYLES ━━ */}
      <style>{`
/* ─── Section ─── */
.ss-section{position:relative;padding:120px 0 140px;overflow:hidden}

/* ─── Header ─── */
.ss-header{text-align:center;margin-bottom:80px;position:relative}
.ss-header>*{opacity:0;transform:translateY(30px);transition:all .8s cubic-bezier(.16,1,.3,1)}
.ss-hdr-vis>*:nth-child(1){opacity:1;transform:none;transition-delay:0ms}
.ss-hdr-vis>*:nth-child(2){opacity:1;transform:none;transition-delay:150ms}
.ss-hdr-vis>*:nth-child(3){opacity:1;transform:none;transition-delay:300ms}

.ss-tag{display:inline-flex;align-items:center;gap:8px;font-size:12px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#06b6d4;background:rgba(6,182,212,.06);border:1px solid rgba(6,182,212,.12);border-radius:50px;padding:8px 20px;margin-bottom:20px}
.ss-tag-dot{width:8px;height:8px;background:#06b6d4;border-radius:50%;animation:ssPulse 2s ease-in-out infinite}
@keyframes ssPulse{0%,100%{opacity:1;box-shadow:0 0 0 0 rgba(6,182,212,.5)}50%{opacity:.6;box-shadow:0 0 0 6px rgba(6,182,212,0)}}

.ss-main-title{font-size:clamp(34px,5vw,56px);font-weight:800;letter-spacing:-1.5px;line-height:1.15;margin-bottom:16px;color:#f0f0f0}
.ss-tw{display:inline-block;opacity:0;transform:translateY(40px);transition:all .9s cubic-bezier(.16,1,.3,1)}
.ss-hdr-vis .ss-tw1{opacity:1;transform:none;transition-delay:100ms}
.ss-hdr-vis .ss-tw2{opacity:1;transform:none;transition-delay:250ms}
.ss-hdr-vis .ss-tw3{opacity:1;transform:none;transition-delay:400ms}
.ss-subtitle{font-size:16px;color:#a0a0a8;max-width:490px;margin:0 auto;line-height:1.7}

/* ─── Sub labels ─── */
.ss-sub{margin-bottom:100px}
.ss-sub-label{display:inline-block;font-size:11px;font-weight:700;color:rgba(6,182,212,.45);letter-spacing:2.5px;text-transform:uppercase;margin-bottom:10px}
.ss-sub-title{font-size:clamp(22px,3vw,30px);font-weight:700;color:#f0f0f0;letter-spacing:-.4px;margin-bottom:40px}
.ss-divider{width:60px;height:2px;margin:0 auto 100px;background:linear-gradient(90deg,transparent,rgba(6,182,212,.15),transparent);border-radius:2px}

/* ─── Three.js container ─── */
.ss-three-section{margin-bottom:60px}
.ss-three-wrap{position:relative;width:100%;height:680px;overflow:hidden}
.ss-three-wrap canvas{display:block;position:absolute;inset:0;z-index:0}
.ss-three-labels{position:absolute;inset:0;pointer-events:none;z-index:1;overflow:hidden}

.ss-3d-label{position:absolute;top:0;left:0;display:flex;align-items:center;gap:6px;padding:4px 12px;background:rgba(5,5,5,.7);border:1px solid rgba(6,182,212,.12);border-radius:7px;font-size:11px;font-weight:600;color:rgba(200,214,229,.75);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);pointer-events:none;white-space:nowrap;transition:opacity .15s;will-change:transform}
.ss-3d-logo{width:16px;height:16px;object-fit:contain;flex-shrink:0}
.ss-3d-icon{font-size:13px}
.ss-3d-center{position:absolute;top:0;left:0;font-size:28px;font-weight:800;color:#06b6d4;letter-spacing:3px;text-shadow:0 0 20px rgba(6,182,212,.5),0 0 60px rgba(6,182,212,.15);pointer-events:none;will-change:transform}

.ss-hint-3d{text-align:center;font-size:12px;color:rgba(160,160,168,.4);margin-top:12px;letter-spacing:.5px;position:relative;z-index:1}

/* ─── 3D Tooltip ─── */
.ss-3d-tip{position:fixed;z-index:200;background:rgba(13,17,23,.95);border:1px solid rgba(6,182,212,.25);border-radius:12px;padding:14px 20px;pointer-events:none;text-align:center;box-shadow:0 12px 40px rgba(0,0,0,.5),0 0 25px rgba(6,182,212,.06);transform:translate(-50%,-130%);opacity:0;transition:opacity .18s;min-width:150px}
.ss-3d-tip.vis{opacity:1}
.ss-3d-tip-logo{width:32px;height:32px;object-fit:contain;margin-bottom:6px}
.ss-3d-tip-icon{font-size:22px;margin-bottom:4px}
.ss-3d-tip-name{font-size:15px;font-weight:700;color:#06b6d4;margin-bottom:2px}
.ss-3d-tip-info{font-size:12px;color:#64748b}

/* ─── TYPING ─── */
.ss-term-bar{display:inline-flex;align-items:center;gap:12px;background:rgba(6,182,212,.04);border:1px solid rgba(6,182,212,.08);border-radius:8px 8px 0 0;padding:10px 18px;font-size:13px;color:#64748b;font-weight:600}
.ss-dots{display:flex;gap:5px}
.ss-dot{width:10px;height:10px;border-radius:50%}
.ss-dot-r{background:#ff5f57}.ss-dot-y{background:#ffbd2e}.ss-dot-g{background:#28c840}
.ss-type-container{display:flex;flex-wrap:wrap;gap:10px;align-items:center;padding:28px 24px;min-height:100px;background:rgba(6,182,212,.015);border:1px solid rgba(6,182,212,.06);border-top:none;border-radius:0 0 12px 12px}
.ss-type-badge{display:inline-flex;align-items:center;padding:8px 18px;border-radius:50px;font-size:14px;font-weight:600;letter-spacing:.3px;border:1px solid rgba(6,182,212,.25);background:rgba(6,182,212,.04);color:#c8d6e5;transition:all .35s ease}
.ss-type-badge.active{border-color:rgba(6,182,212,.6);color:#06b6d4;background:rgba(6,182,212,.08);box-shadow:0 0 18px rgba(6,182,212,.08)}
.ss-type-badge.done{opacity:1}
.ss-type-badge.done:hover{border-color:rgba(6,182,212,.5);background:rgba(6,182,212,.08);transform:translateY(-2px) scale(1.04)}
.ss-cursor{display:inline-block;width:2px;height:17px;background:#06b6d4;margin-left:2px;border-radius:1px;vertical-align:middle;box-shadow:0 0 6px rgba(6,182,212,.6);animation:ssBlink .65s step-end infinite}
@keyframes ssBlink{0%,100%{opacity:1}50%{opacity:0}}

/* ─── PROGRESS GRID & CARDS ─── */
.ss-prog-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:24px;width:100%}
.ss-prog-card{position:relative;background:rgba(255,255,255,0.02);border:1px solid rgba(255,255,255,0.06);border-radius:18px;padding:24px;backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);transition:all 0.45s cubic-bezier(0.25, 0.8, 0.25, 1);overflow:hidden;display:flex;flex-direction:column;justify-content:space-between;height:220px;opacity:0;transform:translateY(30px);animation:ssCardFadeIn 0.8s cubic-bezier(0.16,1,0.3,1) forwards}

@keyframes ssCardFadeIn{
  to { opacity:1; transform:translateY(0); }
}

.ss-prog-card::before{content:'';position:absolute;top:0;left:0;width:100%;height:100%;background:radial-gradient(circle at 80% 20%,var(--card-color-alpha) 0%,transparent 60%);opacity:0.35;transition:opacity 0.4s ease;pointer-events:none}
.ss-prog-card:hover{transform:translateY(-6px);border-color:var(--card-color);box-shadow:0 20px 40px rgba(0,0,0,0.4),0 0 30px var(--card-color-glow)}
.ss-prog-card:hover::before{opacity:0.65}

.ss-card-top{display:flex;justify-content:space-between;align-items:center}
.ss-card-icon-wrap{width:48px;height:48px;border-radius:14px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);display:flex;align-items:center;justify-content:center;color:var(--card-color);transition:all 0.3s cubic-bezier(0.25,0.8,0.25,1)}
.ss-prog-card:hover .ss-card-icon-wrap{background:var(--card-color-alpha2);border-color:var(--card-color);box-shadow:0 0 15px var(--card-color-glow);transform:scale(1.05)}
.ss-card-icon{width:22px;height:22px;stroke-width:2px}

.ss-circle-wrap{position:relative;width:80px;height:80px;display:flex;align-items:center;justify-content:center}
.ss-circle-svg{transform:rotate(-90deg)}
.ss-circle-pct{position:absolute;font-size:16px;font-weight:800;color:#fff;font-variant-numeric:tabular-nums;letter-spacing:0.5px}
.ss-circle-progress{transition:stroke-dashoffset 1.2s cubic-bezier(0.16,1,0.3,1);filter:drop-shadow(0 0 4px var(--card-color))}

.ss-card-bottom{margin-top:14px}
.ss-card-title{font-size:17px;font-weight:700;color:#f0f0f0;margin-bottom:6px;letter-spacing:-0.3px}
.ss-card-subtitle{font-size:13px;color:#8a8a93;line-height:1.4}

/* ─── Responsive ─── */
@media(max-width:991px){
  .ss-three-wrap{height:500px}
  .ss-sub{margin-bottom:80px}
  .ss-prog-grid{grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:20px}
}
@media(max-width:768px){
  .ss-section{padding:80px 0 100px}
  .ss-header{margin-bottom:70px}
  .ss-three-wrap{height:400px}
  .ss-type-container{padding:20px 16px;gap:8px}
  .ss-type-badge{padding:6px 14px;font-size:13px}
  .ss-card-title{font-size:15px}
  .ss-card-subtitle{font-size:12px}
  .ss-prog-card{height:200px;padding:20px}
  .ss-circle-wrap{width:70px;height:70px}
  .ss-circle-svg{width:70px;height:70px}
  .ss-circle-pct{font-size:14px}
}
@media(max-width:575px){
  .ss-three-wrap{height:320px}
  .ss-type-container{padding:16px 12px;gap:6px}
  .ss-type-badge{padding:5px 12px;font-size:12px}
  .ss-3d-label{font-size:10px;padding:3px 8px}
  .ss-3d-center{font-size:22px}
  .ss-prog-grid{grid-template-columns:1fr;gap:16px}
  .ss-prog-card{height:190px}
}
      `}</style>
    </section>
  );
};
